'use client';

import { DISTANCE_UNITS } from '../../constants/constants';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { getPlaceUrl } from '../../helpers/urls';
import { EventMykissCardClicked } from '../../interfaces/events';
import { PlaceCarouselItem } from '../../interfaces/graphql';
import { LinkCard } from '../Cards/LinkCard/LinkCard';
import { ContinuousCarousel } from '../Carousels/ContinuousCarousel/ContinuousCarousel';
import { Container } from '../Container/Container';
import { Heading } from '../Heading/Heading';
import { PinIcon } from '../Icons/PinIcon';
import { WaterImage } from '../WaterImage/WaterImage';

export interface IPlaceCarouselProps {
  dataTestId: string;
  title: string;
  placeEdges?: { distance?: number; distanceUnit?: string; node: PlaceCarouselItem }[];
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
}

export const PlaceCarousel = ({
  dataTestId,
  title,
  placeEdges = [],
  clickEventSource,
  clickEventSourceId,
}: IPlaceCarouselProps) => {
  const { currentPage } = useAnalytics();

  return (
    <Container>
      <ContinuousCarousel dataTestId={dataTestId} title={title} childrenClassName="">
        {placeEdges.map(({ node: place, distance, distanceUnit = DISTANCE_UNITS.MILES }, index) => {
          const { region, slug, _id, country, name } = place;
          const placeText = `${region.name ? `${region.name}, ` : ''}${country.countryName}`;
          return (
            <LinkCard
              key={`${dataTestId}-${_id}`}
              to={getPlaceUrl(country.id, region.regionCode || '', slug || '')}
              eventProperties={{
                source: clickEventSource,
                source_id: clickEventSourceId,
                target: 'place',
                target_id: place._id,
                position: index,
                page: currentPage,
              }}
              className={`overflow-hidden rounded-md md:rounded-lg`}
            >
              {place.unsplashImage ? (
                <div
                  className="bg-lake-fog rounded-md md:rounded-lg"
                  style={{
                    background: `linear-gradient(rgba(0,0,0,0),rgba(0,0,0,0.4)), center / cover no-repeat url("${place.unsplashImage.imageUrl}&w=248&h=155&fit=crop")`,
                    height: 155,
                    width: 248,
                  }}
                />
              ) : (
                <WaterImage
                  latitude={place.latitude.toString()}
                  longitude={place.longitude.toString()}
                  alt={place.name}
                  width={240}
                  height={150}
                />
              )}

              <Heading as="h3" variant="xs" className="mt-4 mb-2 w-60 truncate">
                {name}
              </Heading>

              <div className="text-midnight w-60 truncate">
                <PinIcon size={16} className="inline" />
                <span className="pb-2 pl-2 text-sm font-semibold">{placeText}</span>
              </div>
              {!!distance && (
                <div className="body-text-xs text-midnight mt-2">
                  {`${distance.toFixed(1)} ${distanceUnit} away`}
                </div>
              )}
            </LinkCard>
          );
        })}
      </ContinuousCarousel>
    </Container>
  );
};
